import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { isAppHost } from "@/urls";
import Landing from "@/marketing/Landing";
import { BetaProvider } from "@/beta/BetaContext";
import { BetaGate } from "@/beta/BetaGate";
// Batch 134 — the privacy policy and the terms of service. EAGER, like Landing
// and unlike App: they are two static pages with no data fetching, and a lazy
// chunk would put a Suspense fallback in front of a document someone was sent
// to read before paying. They are also the pages Stripe and a regulator load
// first, so they must not depend on a chunk request succeeding.
import PrivacyPolicy from "@/legal/PrivacyPolicy";
import TermsOfService from "@/legal/TermsOfService";

// Route-based code splitting. The heavy chat application (App.js + constants +
// all components) stays lazy so neither host pays for it up front. The marketing
// Landing, however, is EAGERLY bundled into the entry chunk: on mobile a second
// lazy round-trip for the Landing chunk added ~1.5s of black RouteFallback after
// main.js executed. Eager import lets the hero paint the moment the entry bundle
// runs — no chunk waterfall, no fallback splash for marketing visitors.
//   app.sparkcue.co        -> the SparkCue application at the root (lazy App)
//   sparkcue.co / preview  -> marketing at "/" (instant), app still at "/app" (lazy)
const App = lazy(() => import("@/App"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

// The chat app wrapped in the beta access gate. When BETA_ACCESS_ENFORCED is off
// (config), the gate is a transparent pass-through, so this is a no-op then.
const GatedApp = () => (
  <BetaProvider>
    <BetaGate>
      <App />
    </BetaGate>
  </BetaProvider>
);

// Minimal, theme-matching paint while the lazy APP chunk loads. Marketing no
// longer uses this (it renders synchronously).
function RouteFallback() {
  return (
    <div
      aria-hidden="true"
      style={{ minHeight: "100vh", backgroundColor: "#0D0D0F" }}
    />
  );
}

export default function Root() {
  if (isAppHost()) {
    return (
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
            {/* Batch 134 — ABOVE the catch-all, and that ordering is the whole
                point. "/*" matches everything, so a legal route declared after
                it never renders and the reader silently gets the application
                instead of the policy. Pinned by legalPages.test.js. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/*" element={<GatedApp />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        {/* Batch 134 — the SAME paths in the second table. The two tables share
            no parent, so a page declared in one is unreachable on the other
            host. A relative /privacy then works from the app, the paywall and
            the marketing footer without any of them knowing where they are. */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AdminApp />
            </Suspense>
          }
        />
        <Route
          path="/app/*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <GatedApp />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
